import { decamelizeKeys } from 'humps';

import {
  AccountFormToken,
  Auth,
  AutoAuth,
  LoginParams,
  SetupParams
} from '../types/Auth';

import { baseApi } from './baseApi';

export const authApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['Patient', 'Procedure'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      login: builder.mutation<Auth, LoginParams>({
        query: (params) => ({
          url: 'api/v4/auth',
          method: 'POST',
          body: params,
        }),
      }),
      autoAuth: builder.mutation<AutoAuth, { mode?: string; }>({
        query: (params) => ({
          url: 'api/v4/auto_auth',
          method: 'POST',
          body: params,
        }),
      }),
      accountForm: builder.mutation<AccountFormToken, { token: string, domain?: string }>({
        query: (params) => ({
          url: 'api/v4/patient_account_form_token',
          method: 'POST',
          body: params,
        }),
      }),
      signup: builder.mutation<Auth, SetupParams>({
        query: ({ token, fields, idempotencyKey, device = '' }) => {
          const params = { token, fields: JSON.stringify(decamelizeKeys(fields)), idempotency_key: idempotencyKey, device };

          return {
            url: 'api/v4/patient_create',
            method: 'POST',
            body: params,
          };
        },
      }),
      setup: builder.mutation<Auth, SetupParams>({
        query: ({ token, fields, idempotencyKey }) => {
          const params = {
            one_use_token: token,
            fields: JSON.stringify(decamelizeKeys(fields)),
            idempotency_key: idempotencyKey,
          };

          return {
            url: 'api/v4/patient/setup',
            method: 'POST',
            body: params,
          };
        },
      }),
      setupTwoFA: builder.mutation({
        query: (params) => {
          return {
            url: 'api/v4/setup_verification',
            method: 'POST',
            body: decamelizeKeys(params),
          };
        },
        invalidatesTags: [
          'Patient',
        ],
      }),
      sendTwoFACode: builder.mutation({
        query: (params) => {
          return {
            url: 'api/v4/start_verification',
            method: 'POST',
            body: decamelizeKeys(params),
          };
        },
      }),
      checkTwoFACode: builder.mutation({
        query: (params) => {
          return {
            url: 'api/v4/check_verification',
            method: 'POST',
            body: decamelizeKeys(params),
          };
        },
      }),
      recaptcha: builder.mutation({
        query: (token) => ({
          url: '/api/v4/recaptcha',
          method: 'POST',
          body: { token },
        }),
      }),
      requestPassword: builder.mutation({
        query: (params) => ({
          url: '/api/v4/request_password',
          method: 'POST',
          body: params,
        }),
      }),
      resetPassword: builder.mutation({
        query: (params) => ({
          url: 'api/v4/reset_password',
          method: 'POST',
          body: params,
        }),
      }),
    }),
    overrideExisting: false,
  });

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useAutoAuthMutation,
  useAccountFormMutation,
  useCheckTwoFACodeMutation,
  useSignupMutation,
  useSendTwoFACodeMutation,
  useSetupMutation,
  useSetupTwoFAMutation,
  useRecaptchaMutation,
  useRequestPasswordMutation,
  useResetPasswordMutation,
} = authApi;
